<template>
    <div class="annual-workflow-cards">
        <div class="section-header">
            <div class="section-title">워크플로우</div>
        </div>
        <div class="cards-container">
            <div class="cards-scroll-area">
                <div class="cards-list">
                    <div v-for="(step, index) in steps" :key="step.workflowIdx ?? step.year" class="card-wrapper">
                        <div
                            class="workflow-card"
                            :class="[
                                `is-${(step.status || '').toLowerCase()}`,
                                { 'is-selected': selectedYear === step.year },
                            ]"
                            @click="handleStepClick(step)"
                        >
                            <div class="content-header">
                                <div class="header-top">
                                    <div class="workflow-name">
                                        {{ step.workflow?.workflowName || `${step.year}년 탄소배출 산정` }}
                                    </div>
                                    <CustomDropdown :items="getWorkflowMenuItems(step)" align="right" />
                                </div>
                                <div class="header-meta-row">
                                    <div class="workflow-meta">
                                        <div class="workflow-period">
                                            <i class="fas fa-calendar-alt"></i>
                                            <span>{{
                                                formatPeriod(step.workflow?.fromMonth, step.workflow?.toMonth)
                                            }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="progress-section">
                                <div class="workflow-progress-header">
                                    <span class="progress-label">진행률</span>
                                    <span class="status-badge" :class="`status-${(step.status || '').toLowerCase()}`">
                                        {{ getStatusLabel(step.status) }}
                                    </span>
                                </div>
                                <div class="progress-bar-wrap">
                                    <div
                                        class="progress-bar-fill"
                                        :style="{ width: getProgressPercent(step) + '%' }"
                                    ></div>
                                </div>
                                <div class="progress-footer">
                                    <span class="progress-value">{{ getProgressPercent(step) }}%</span>
                                </div>
                            </div>

                            <div
                                v-if="step.status === 'CONFIRMED' && step.workflow?.complianceSummary"
                                class="compliance-section"
                            >
                                <div class="compliance-header">
                                    <span class="compliance-label">이행 정산</span>
                                    <span
                                        class="compliance-badge"
                                        :class="`cs-${(step.workflow.complianceStat || 'PENDING').toLowerCase()}`"
                                    >
                                        {{ getComplianceStatLabel(step.workflow.complianceStat) }}
                                    </span>
                                </div>
                                <div class="compliance-metrics small">
                                    <span>허용 {{ formatT(step.workflow.complianceSummary.assignedCapT) }} t</span>
                                    <span>실측 {{ formatT(step.workflow.complianceSummary.grossActualT) }} t</span>
                                    <span v-if="step.workflow.complianceSummary.netPositionT != null">
                                        순 {{ formatT(step.workflow.complianceSummary.netPositionT) }} t
                                    </span>
                                </div>
                            </div>

                            <div v-if="hasEmissions(step)" class="emissions-section">
                                <div class="emissions-total">
                                    <span class="emissions-value"
                                        >{{ (step.workflow?.totalEmissions).toLocaleString() }} kgCO₂e</span
                                    >
                                </div>
                                <div class="emissions-scopes">
                                    <span v-if="scopeNum(step, 1) !== null" class="scope-badge">
                                        <span class="scope-badge__label">S1</span>
                                        <span class="scope-badge__value">{{
                                            formatCarbonAutoWithUnit(scopeNum(step, 1), "kgCO2e", 1)
                                        }}</span>
                                    </span>
                                    <span v-if="scopeNum(step, 2) !== null" class="scope-badge">
                                        <span class="scope-badge__label">S2</span>
                                        <span class="scope-badge__value">{{
                                            formatCarbonAutoWithUnit(scopeNum(step, 2), "kgCO2e", 1)
                                        }}</span>
                                    </span>
                                    <span v-if="scopeNum(step, 3) !== null" class="scope-badge">
                                        <span class="scope-badge__label">S3</span>
                                        <span class="scope-badge__value">{{
                                            formatCarbonAutoWithUnit(scopeNum(step, 3), "kgCO2e", 1)
                                        }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- 카드 사이 화살표 -->
                        <div v-if="index < steps.length - 1" class="card-arrow">
                            <i class="fas fa-chevron-right"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 워크플로우 생성/수정 모달 -->
        <WorkflowFormModal
            ref="workflowModal"
            :is-edit-mode="isEditMode"
            :initial-data="formData"
            :org-options="orgOptions"
            :saving="saving"
            @save="handleSave"
            @hidden="handleModalHidden"
        />

        <!-- 삭제 확인 모달 -->
        <b-modal ref="deleteModal" title="워크플로우 삭제" size="sm" @ok="handleDelete">
            <strong>{{ selectedStep?.year ?? "-" }}년</strong> 워크플로우가 삭제됩니다.
        </b-modal>

        <!-- 증빙자료 누락 → 강제생성/강제 확정 확인 모달 -->
        <EvidenceForceConfirmModal
            ref="evidenceModal"
            :error="evidenceModalError"
            :title="evidenceModalPayload?.type === 'confirm' ? '강제 확정' : '워크플로우 강제 생성'"
            subtitle="데이터 정합성 오류가 감지되었습니다."
            :confirm-button-text="evidenceModalPayload?.type === 'confirm' ? '강제 확정' : '강제생성'"
            @confirm="handleForceConfirm"
            @hidden="onEvidenceModalHidden"
        />

        <!-- 보고서 생성 모달 -->
        <ReportCreateModal ref="reportModal" :workflow-idx="null" :workflow-name="''" />

        <!-- 보고서 관리 모달 -->
        <ReportModal
            ref="reportManagementModal"
            :loginUser="loginUser"
            :workflow-idx="reportManagementModalWorkflowIdx"
            :workflow-name="reportManagementModalWorkflowName"
            @report-generated="handleReportGenerated"
        />

        <ComplianceSettlementModal
            ref="complianceModal"
            :workflow-idx="complianceModalWorkflowIdx"
            :workflow-name="complianceModalWorkflowName"
            @settled="$emit('refresh')"
            @rolled-back="$emit('refresh')"
        />
    </div>
</template>

<script>
/**
 * AnnualWorkflowCards
 * 연간 워크플로우 카드 목록 컴포넌트
 * - 년도별 워크플로우 카드 표시 (진행률, 배출량)
 * - 드롭다운: 편집, 상태 변경(periodStat), 삭제
 * - 생성/수정 모달, 삭제 확인 모달
 */
import CarbonCodes from "../../../codes";
import carbonApi from "../../../api";
import { unwrapResponse, extractErrorMessage } from "../../../utils/api.js";
import WorkflowFormModal from "../modals/WorkflowFormModal.vue";
import ReportCreateModal from "../modals/ReportCreateModal.vue";
import ReportModal from "../../report/ReportModal.vue";
import { CustomDropdown } from "../../common";
import EvidenceForceConfirmModal from "../../common/EvidenceForceConfirmModal.vue";
import ComplianceSettlementModal from "../modals/ComplianceSettlementModal.vue";
import { formatCarbonAutoWithUnit } from "../../../utils/convertUnit.js";

export default {
    name: "AnnualWorkflowCards",
    components: {
        WorkflowFormModal,
        ReportCreateModal,
        ReportModal,
        CustomDropdown,
        EvidenceForceConfirmModal,
        ComplianceSettlementModal,
    },
    props: {
        steps: { type: Array, required: true },
        selectedYear: { type: Number, default: null },
        orgOptions: { type: Array, default: () => [] },
        loginUser: { type: Object, default: null },
    },
    data() {
        return {
            isEditMode: false,
            saving: false,
            selectedStep: null,
            formData: {},
            reportManagementModalWorkflowIdx: null,
            reportManagementModalWorkflowName: "",
            evidenceModalError: null,
            evidenceModalPayload: null,
            complianceModalWorkflowIdx: null,
            complianceModalWorkflowName: "",
        };
    },
    methods: {
        formatT(v) {
            const n = Number(v);
            if (!Number.isFinite(n)) return "—";
            return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
        },
        getComplianceStatLabel(stat) {
            return CarbonCodes.complianceStat.getLabel(stat) || stat || "미정산";
        },
        openComplianceModal(step) {
            this.complianceModalWorkflowIdx = this.getWorkflowIdx(step);
            this.complianceModalWorkflowName =
                step.workflow?.workflowName || (step.year ? `${step.year}년 탄소배출 산정` : "");
            this.$nextTick(() => this.$refs.complianceModal?.show());
        },
        async rollbackComplianceSettlement(step) {
            const idx = this.getWorkflowIdx(step);
            if (!idx) return;
            const ok = await this.$bvModal.msgBoxConfirm(
                [
                    "이행 정산(이월·차입·상쇄)을 롤백합니다.",
                    "산정 확정 상태는 유지되며, 익년에 이미 이월 사용·정산이 있으면 거부됩니다.",
                    "계속하시겠습니까?",
                ].join("\n"),
                {
                    title: "이행 정산 롤백",
                    okTitle: "롤백",
                    cancelTitle: "취소",
                    okVariant: "warning",
                    centered: true,
                },
            );
            if (!ok) return;
            await this.callWorkflowApi(() => carbonApi.workflow.rollbackComplianceSettlement(idx), {
                successMsg: "이행 정산이 롤백되었습니다.",
                errorMsg: "이행 정산 롤백에 실패했습니다.",
            });
        },
        /** API 호출 + 성공/실패 토스트 + refresh 이벤트 공통 처리 */
        async callWorkflowApi(
            apiFn,
            {
                successMsg,
                errorMsg = "요청에 실패했습니다.",
                emitRefresh = true,
                successToastOpts = {},
                errorToastOpts = {},
            } = {},
        ) {
            try {
                const response = await apiFn();
                const result = unwrapResponse(response, { defaultMessage: errorMsg, includeMessage: true });
                if (emitRefresh) this.$emit("refresh");
                this.$bvToast.toast(result?.message || successMsg, { variant: "success", ...successToastOpts });
                return result;
            } catch (e) {
                const msg = extractErrorMessage(e, errorMsg);
                this.$bvToast.toast(msg, { variant: "danger", ...errorToastOpts });
                throw e;
            }
        },
        getStatusLabel(status) {
            return CarbonCodes.periodStat.getLabel(status);
        },
        getProgressPercent(step) {
            if (step.status === "CANCELLED") return 0;
            const workflowSteps = step.workflow?.steps;
            if (Array.isArray(workflowSteps) && workflowSteps.length > 0) {
                const verifiedCount = workflowSteps.filter((s) => s.calcStat === "VERIFIED").length;
                return Math.round((verifiedCount / workflowSteps.length) * 100);
            }
            const apiProgress = step.workflow?.progress?.overall;
            if (typeof apiProgress === "number") return apiProgress;
            const map = { READY: 0, COLLECTING: 30, CALCULATED: 70, VERIFIED: 90, CONFIRMED: 100 };
            return map[step.status] ?? 0;
        },
        formatPeriod(fromMonth, toMonth) {
            if (!fromMonth || !toMonth) return "-";
            const from = `${fromMonth.substring(0, 4)}.${fromMonth.substring(4, 6)}`;
            const to = `${toMonth.substring(0, 4)}.${toMonth.substring(4, 6)}`;
            return `${from} ~ ${to}`;
        },
        hasEmissions(step) {
            const w = step.workflow;
            return (
                w &&
                (w.totalEmissions != null ||
                    w.scope1Emissions != null ||
                    w.scope2Emissions != null ||
                    w.scope3Emissions != null)
            );
        },
        scopeNum(step, scope) {
            const w = step.workflow;
            if (!w) return null;
            if (scope === 1) return w.scope1Emissions;
            if (scope === 2) return w.scope2Emissions;
            if (scope === 3) return w.scope3Emissions;
            return null;
        },
        formatCarbonAutoWithUnit,
        handleStepClick(step) {
            this.$emit("step-click", step);
        },
        getWorkflowIdx(step) {
            return step.workflowIdx ?? step.workflow?.workflowIdx;
        },
        /** 상태(periodStat)별로 표시할 드롭다운 항목 동적 생성
         * 미시작(READY): 수집중, 취소 | 수집중(COLLECTING): 산정완료, 취소 | 산정완료(CALCULATED): 검증완료, 취소
         * 검증완료(VERIFIED): 확정완료, reopen | 확정완료(CONFIRMED): reopen만 | 취소(CANCELLED): reopen만
         */
        getStatusChangeSubItems(step, status) {
            const items = [];
            const add = (value, text, handler) => items.push({ value, text, onClick: handler });

            if (status === "READY") {
                add("COLLECTING", CarbonCodes.periodStat.getLabel("COLLECTING"), () =>
                    this.updateWorkflowStatus(step, "COLLECTING"),
                );
                add("CANCELLED", CarbonCodes.periodStat.getLabel("CANCELLED"), () => this.cancelWorkflow(step));
            } else if (status === "COLLECTING") {
                add("CALCULATED", CarbonCodes.periodStat.getLabel("CALCULATED"), () =>
                    this.updateWorkflowStatus(step, "CALCULATED"),
                );
                add("CANCELLED", CarbonCodes.periodStat.getLabel("CANCELLED"), () => this.cancelWorkflow(step));
            } else if (status === "CALCULATED") {
                add("VERIFIED", CarbonCodes.periodStat.getLabel("VERIFIED"), () => this.verifyWorkflow(step));
                add("CANCELLED", CarbonCodes.periodStat.getLabel("CANCELLED"), () => this.cancelWorkflow(step));
            } else if (status === "VERIFIED") {
                add("CONFIRMED", CarbonCodes.periodStat.getLabel("CONFIRMED"), () => this.confirmWorkflow(step));
                add("REOPEN", "재오픈", () => this.reopenWorkflow(step));
            } else if (status === "CONFIRMED" || status === "CANCELLED") {
                add("REOPEN", "재오픈", () => this.reopenWorkflow(step));
            }
            return items;
        },
        getWorkflowMenuItems(step) {
            const status = (step.status || "").toUpperCase();
            const statusSubItems = this.getStatusChangeSubItems(step, status);
            return [
                { icon: "fa-edit", label: "편집", variant: "default", onClick: () => this.openEditModal(step) },
                {
                    icon: "fa-check",
                    label: "상태 변경",
                    variant: "default",
                    subItems: statusSubItems,
                },
                // {
                //     icon: "fa-file-alt",
                //     label: "보고서 생성",
                //     variant: "default",
                //     onClick: () => this.openReportModal(step),
                // },
                ...(step.status === "VERIFIED" || step.status === "CONFIRMED"
                    ? [
                          {
                              icon: "fa-folder-open",
                              label: "보고서 관리",
                              variant: "default",
                              onClick: () => this.openReportManagementModal(step),
                          },
                      ]
                    : []),
                ...(step.status === "CONFIRMED"
                    ? [
                          {
                              icon: "fa-balance-scale",
                              label: "이행 정산",
                              variant: "default",
                              onClick: () => this.openComplianceModal(step),
                          },
                          ...(String(step.workflow?.complianceStat || "").toUpperCase() === "SETTLED"
                              ? [
                                    {
                                        icon: "fa-undo",
                                        label: "이행 정산 롤백",
                                        variant: "default",
                                        onClick: () => this.rollbackComplianceSettlement(step),
                                    },
                                ]
                              : []),
                      ]
                    : []),
                { icon: "fa-trash", label: "삭제", variant: "danger", onClick: () => this.openDeleteModal(step) },
            ];
        },
        openReportManagementModal(step) {
            this.reportManagementModalWorkflowIdx = step ? this.getWorkflowIdx(step) : null;
            this.reportManagementModalWorkflowName = step
                ? step.workflow?.workflowName || (step.year ? `${step.year}년 탄소배출 산정` : "")
                : "";
            this.$nextTick(() => {
                this.$refs.reportManagementModal?.show();
            });
        },
        handleReportGenerated() {
            this.$bvToast.toast("보고서가 생성되었습니다.", { variant: "success", title: "완료" });
            this.$emit("report-generated");
        },
        async updateWorkflowStatus(step, periodStat) {
            const idx = this.getWorkflowIdx(step);
            if (!idx) return;
            await this.callWorkflowApi(() => carbonApi.workflow.updateInventoryWorkflowStatus(idx, { periodStat }), {
                successMsg: "상태가 변경되었습니다.",
                errorMsg: "상태 변경에 실패했습니다.",
            });
        },
        async verifyWorkflow(step) {
            const idx = this.getWorkflowIdx(step);
            if (!idx) return;
            await this.callWorkflowApi(() => carbonApi.workflow.verifyInventoryWorkflow(idx), {
                successMsg: "워크플로우가 검증되었습니다.",
                errorMsg: "검증에 실패했습니다.",
            });
        },
        async confirmWorkflow(step) {
            const idx = this.getWorkflowIdx(step);
            if (!idx) return;
            try {
                await this.callWorkflowApi(() => carbonApi.workflow.confirmInventoryWorkflow(idx), {
                    successMsg: "워크플로우가 확정되었습니다.",
                    errorMsg: "확정에 실패했습니다.",
                });
            } catch (e) {
                const data = e?.response?.data ?? e?.payload ?? null;
                const err = data?.error ?? data;
                if (
                    (e?.response?.status === 409 || data?.success === false) &&
                    err?.code === "EVIDENCE_MISSING" &&
                    err?.canForceConfirm === true
                ) {
                    this.evidenceModalError = err;
                    this.evidenceModalPayload = { type: "confirm", workflowIdx: idx };
                    this.$refs.evidenceModal?.show();
                } else {
                    throw e;
                }
            }
        },
        async reopenWorkflow(step) {
            const idx = this.getWorkflowIdx(step);
            if (!idx) return;
            if (!confirm("이 워크플로우를 재오픈하시겠습니까?")) return;
            await this.callWorkflowApi(() => carbonApi.workflow.reopenInventoryWorkflow(idx), {
                successMsg: "워크플로우가 재오픈되었습니다.",
                errorMsg: "재오픈에 실패했습니다.",
            });
        },
        async cancelWorkflow(step) {
            const idx = this.getWorkflowIdx(step);
            if (!idx) return;
            if (!confirm("이 워크플로우를 취소하시겠습니까?")) return;
            await this.callWorkflowApi(() => carbonApi.workflow.cancelInventoryWorkflow(idx), {
                successMsg: "워크플로우가 취소되었습니다.",
                errorMsg: "취소에 실패했습니다.",
            });
        },
        openCreateModal() {
            this.isEditMode = false;
            this.selectedStep = null;
            this.formData = {};
            this.$refs.workflowModal.show();
        },
        openEditModal(step) {
            this.isEditMode = true;
            this.selectedStep = step;
            const workflow = step.workflow || {};
            this.formData = {
                orgIdx: workflow.orgIdx || null,
                workflowIdx: workflow.workflowIdx || null,
                pathwayIdx: workflow.pathwayIdx != null ? Number(workflow.pathwayIdx) : null,
                periodStat: workflow.periodStat || step.status || null,
                workflowName: workflow.workflowName || "",
                fromMonth: workflow.fromMonth || "",
                toMonth: workflow.toMonth || "",
                remarks: workflow.remarks || "",
            };
            this.$refs.workflowModal.show();
        },
        openDeleteModal(step) {
            this.selectedStep = step;
            this.$refs.deleteModal.show();
        },
        handleModalHidden() {
            this.selectedStep = null;
            this.isEditMode = false;
            this.formData = {};
        },
        async handleSave(payload) {
            try {
                this.saving = true;
                const isEdit = this.isEditMode && this.selectedStep?.workflowIdx;
                if (isEdit) {
                    await this.callWorkflowApi(
                        () => carbonApi.workflow.updateInventoryWorkflow(this.selectedStep.workflowIdx, payload),
                        {
                            successMsg: "워크플로우가 수정되었습니다.",
                            errorMsg: "워크플로우 수정에 실패했습니다.",
                            successToastOpts: { title: "수정 완료", autoHideDelay: 3000 },
                            errorToastOpts: { title: "저장 실패", autoHideDelay: 5000 },
                        },
                    );
                    this.$refs.workflowModal.hide();
                } else {
                    try {
                        const response = await carbonApi.workflow.createInventoryWorkflow(payload);
                        unwrapResponse(response);
                        this.$emit("refresh");
                        this.$bvToast.toast("워크플로우가 생성되었습니다.", {
                            variant: "success",
                            title: "생성 완료",
                            autoHideDelay: 3000,
                        });
                        this.$refs.workflowModal.hide();
                    } catch (e) {
                        const data = e?.response?.data ?? e?.payload ?? null;
                        const err = data?.error ?? data;
                        if (
                            (e?.response?.status === 409 || data?.success === false) &&
                            err?.code === "EVIDENCE_MISSING" &&
                            err?.canForceConfirm === true
                        ) {
                            this.evidenceModalError = err;
                            this.evidenceModalPayload = { type: "create", payload };
                            this.$refs.evidenceModal?.show();
                        } else {
                            this.$bvToast.toast(extractErrorMessage(e, "워크플로우 생성에 실패했습니다."), {
                                variant: "danger",
                                title: "저장 실패",
                                autoHideDelay: 5000,
                            });
                        }
                    }
                }
            } catch (e) {
                // 수정 시 토스트는 callWorkflowApi에서 이미 표시됨
            } finally {
                this.saving = false;
            }
        },
        async handleForceConfirm(forceConfirmValue) {
            if (!this.evidenceModalPayload) return;
            const { type } = this.evidenceModalPayload;
            try {
                this.saving = true;
                if (type === "create") {
                    const payload = { ...this.evidenceModalPayload.payload, forceConfirm: forceConfirmValue };
                    const response = await carbonApi.workflow.createInventoryWorkflow(payload);
                    unwrapResponse(response);
                    this.$refs.workflowModal?.hide();
                    this.$bvToast.toast("워크플로우가 생성되었습니다.", {
                        variant: "success",
                        title: "생성 완료",
                        autoHideDelay: 3000,
                    });
                } else if (type === "confirm") {
                    const { workflowIdx } = this.evidenceModalPayload;
                    const response = await carbonApi.workflow.confirmInventoryWorkflow(workflowIdx, {
                        forceConfirm: forceConfirmValue,
                    });
                    unwrapResponse(response);
                    this.$bvToast.toast("워크플로우가 확정되었습니다.", {
                        variant: "success",
                        title: "확정 완료",
                        autoHideDelay: 3000,
                    });
                }
                this.evidenceModalError = null;
                this.evidenceModalPayload = null;
                this.$refs.evidenceModal?.hide();
                this.$emit("refresh");
            } catch (e) {
                this.$bvToast.toast(
                    extractErrorMessage(
                        e,
                        type === "confirm" ? "강제 확정에 실패했습니다." : "강제 생성에 실패했습니다.",
                    ),
                    { variant: "danger", title: "저장 실패", autoHideDelay: 5000 },
                );
            } finally {
                this.saving = false;
            }
        },
        onEvidenceModalHidden() {
            this.evidenceModalError = null;
            this.evidenceModalPayload = null;
        },
        async handleDelete() {
            if (!this.selectedStep?.workflowIdx) {
                this.$bvToast.toast("삭제할 워크플로우를 찾을 수 없습니다.", {
                    title: "삭제 실패",
                    variant: "danger",
                    autoHideDelay: 3000,
                });
                return;
            }
            try {
                await this.callWorkflowApi(
                    () => carbonApi.workflow.deleteInventoryWorkflow(this.selectedStep.workflowIdx),
                    {
                        successMsg: "워크플로우가 삭제되었습니다.",
                        errorMsg: "워크플로우 삭제에 실패했습니다.",
                        successToastOpts: { title: "삭제 완료", autoHideDelay: 3000 },
                        errorToastOpts: { title: "삭제 실패", autoHideDelay: 5000 },
                    },
                );
            } catch (e) {
                // 토스트는 callWorkflowApi에서 이미 표시됨
            }
        },
    },
};
</script>

<style scoped>
.annual-workflow-cards {
    width: 100%;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.section-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: 0.5px;
    padding-left: 12px;
    position: relative;
}

.section-title::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px;
    background: linear-gradient(180deg, #b45309 0%, #d97706 100%);
    border-radius: 2px;
}

.cards-container {
    position: relative;
    width: 100%;
    overflow: hidden;
}

.cards-scroll-area {
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
}

.cards-scroll-area::-webkit-scrollbar {
    height: 8px;
}

.cards-scroll-area::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

.cards-scroll-area::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.cards-scroll-area::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.cards-scroll-area::-webkit-scrollbar-thumb:active {
    background: #d97706;
}

.cards-list {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: fit-content;
    justify-content: flex-start;
    padding: 12px;
}

.card-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
}

.workflow-card {
    width: 280px;
    padding: 10px 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    text-align: left;
    flex-shrink: 0;
}

.workflow-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.workflow-card.is-selected {
    border-color: #d97706;
    background: linear-gradient(135deg, #ffffff 0%, #fffbeb 100%);
    box-shadow:
        0 0 0 2px rgba(217, 119, 6, 0.25),
        0 8px 24px rgba(217, 119, 6, 0.12),
        0 4px 12px rgba(0, 0, 0, 0.06);
}

.content-header {
    margin-bottom: 4px;
}

.header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.header-meta-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.workflow-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.workflow-org,
.workflow-period {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #94a3b8;
    font-weight: 600;
}

.workflow-org i,
.workflow-period i {
    font-size: 11px;
    color: #94a3b8;
}

.progress-section {
    margin-top: 0;
}

.workflow-progress-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}

.progress-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.progress-bar-wrap {
    height: 8px;
    background: #f1f5f9;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #10b981, #34d399);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.workflow-card.is-cancelled .progress-bar-fill {
    background: #cbd5e1;
}

.progress-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.progress-value {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
}

.workflow-card.is-cancelled .progress-value {
    color: #94a3b8;
}

/* 배출량 (강조 / 보조 구분) */
.compliance-section {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #e2e8f0;
}

.compliance-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
}

.compliance-label {
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
}

.compliance-badge {
    font-size: 9px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
}

.compliance-badge.cs-pending,
.compliance-badge.cs-flex_draft {
    background: #fef3c7;
    color: #92400e;
}

.compliance-badge.cs-settled {
    background: #d1fae5;
    color: #065f46;
}

.compliance-metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    color: #475569;
}

.emissions-section {
    margin-top: 6px;
    border-top: 1px solid #f1f5f9;
}

.emissions-total {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 6px;
}

.emissions-value {
    font-size: 15px;
    font-weight: 700;
    color: #059669;
    letter-spacing: -0.02em;
}

.emissions-scopes {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.scope-badge {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    padding: 2px 8px;
    font-size: 10px;
    background: #f1f5f9;
    border-radius: 4px;
}

.scope-badge__label {
    font-weight: 500;
    color: #94a3b8;
}

.scope-badge__value {
    font-weight: 600;
    color: #475569;
}

.workflow-name {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
    white-space: nowrap;
    letter-spacing: 0.2px;
    flex-shrink: 0;
}

.status-ready {
    background: #f1f5f9;
    color: #64748b;
}

.status-collecting {
    background: #dbeafe;
    color: #1e40af;
}

.status-calculated {
    background: #fed7aa;
    color: #9a3412;
}

.status-verified {
    background: #10b981;
    color: #ffffff;
}

.status-confirmed {
    background: #10b981;
    color: #ffffff;
}

.status-cancelled {
    background: #ef4444;
    color: #ffffff;
}

.card-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #cbd5e1;
    font-size: 20px;
    flex-shrink: 0;
}

.card-arrow i {
    transition: all 0.2s ease;
}

.card-wrapper:hover .card-arrow i {
    color: #d97706;
    transform: translateX(4px);
}
</style>
